import posthog from 'posthog-js';
import { getPerfArms, getPerfArmProperties } from 'src/lib/perf/perfArms';
import { getPerfSampling } from 'src/lib/perf/perfSampling';
import { enrichPerfEvent, setPosthogRecordingReader } from 'src/lib/perf/perfContext';
import { PERF_ARMS_OFF_FLAG, PERF_ARMS_OFF_STORAGE_KEY } from 'src/lib/perf/perfArms.constants';
import type { tAnalyticsCapture, tAnalyticsProvider } from '../analytics.types';
import {
  buildIdentityDigest,
  readIdentityHashEntry,
  writeIdentityHashEntry,
  type tIdentityDigest,
  type tIdentityHashEntry,
  type tIdentityHashKeyOptions,
} from '../identityHashStore';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from '../../logging';

const LOG_CTX = { component: LOG_COMPONENTS.API, method: 'posthog', operation: LOG_OPERATIONS.ERROR };

// $web_vitals was ~10% of total event volume (3.2M/mo) with remote config
// enabling it for every session. Sampled per PAGE LOAD (the decision is made
// once at init, and init runs on every full page load): one in ten page loads
// captures vitals. Multiply counts by 10 when reading volumes.

// Unchanged identity properties are sent again after this long, so a send the
// SDK accepted but PostHog never ingested is repaired within a week.
const IDENTITY_RESEND_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

type tSendDecision =
  | { kind: 'full' }
  | { kind: 'none' }
  | { kind: 'changed'; changedProperties: Record<string, unknown> };

/**
 * Decides what to send for a property set the SDK already holds for this
 * identity: nothing, every property (resend interval reached), or the keys
 * whose hash changed.
 */
function decideSend(
  properties: Record<string, unknown>,
  options: { digest: tIdentityDigest; entry: tIdentityHashEntry },
): tSendDecision {
  const { digest, entry } = options;
  if (digest.hash === entry.hash) {
    return Date.now() - entry.sentAt >= IDENTITY_RESEND_INTERVAL_MS
      ? { kind: 'full' }
      : { kind: 'none' };
  }
  const changedProperties: Record<string, unknown> = {};
  Object.keys(digest.propertyHashes)
    .filter((key) => digest.propertyHashes[key] !== entry.propertyHashes[key])
    .forEach((key) => {
      changedProperties[key] = properties[key];
    });
  return { kind: 'changed', changedProperties };
}

export class PostHogProvider implements tAnalyticsProvider {
  constructor(key: string, host?: string) {
    try {
      setPosthogRecordingReader(() => posthog.sessionRecordingStarted());
      posthog.init(key, {
        api_host: host ?? 'https://us.i.posthog.com',
        persistence: 'localStorage+cookie',
        autocapture: false,
        capture_pageview: false,
        // $pageleave defaults ON and pairs with $pageview (which we disable);
        // with capture_pageview:false it carries no funnel value, so drop it.
        capture_pageleave: false,
        disable_session_recording: true,
        enable_recording_console_log: false,
        capture_performance: {
          // Request URLs carry lead phones (search queries, chat handles, avatar
          // paths); an explicit false wins over the project network setting.
          network_timing: false,
          web_vitals: getPerfSampling().webVitals,
        },
        before_send: (event) => enrichPerfEvent(event, () => posthog.sessionRecordingStarted()),
        session_recording: {
          maskAllInputs: true,
          // Recordings show chat text, lead names and phones; local options
          // win over the project masking setting, so a reset there cannot unmask.
          maskTextSelector: '*',
          // The project can still turn on header or body capture, which loads
          // the network log; returning null drops each request from it.
          maskCapturedNetworkRequestFn: () => null,
          // Blocked elements keep only their class and size, so lead photos and
          // the phone in an avatar src or a tel: href never reach a replay.
          blockSelector: 'img, video, a[href^="tel:"]',
          blockClass: 'ph-no-capture',
          inlineStylesheet: true,
          collectFonts: false,
          recordCrossOriginIframes: false,
        },
      });
      this.registerForSession(getPerfArmProperties());
      this.onSessionId(() => this.registerForSession(getPerfArmProperties()));
      this.onFeatureFlags((flags) => {
        const disabled = flags[PERF_ARMS_OFF_FLAG];
        if (typeof disabled !== 'boolean') return;
        try {
          localStorage.setItem(PERF_ARMS_OFF_STORAGE_KEY, String(disabled));
        } catch {
          // Storage can be unavailable; perfArms already falls back to control.
        }
      });
    } catch (error) {
      LLog.error(LOG_CTX, 'PostHog init failed', error as Error);
    }
  }

  identify(userId: string, properties?: Record<string, unknown>, onceProperties?: Record<string, unknown>): void {
    try {
      const personProperties = properties ?? {};
      const digest = buildIdentityDigest(personProperties);
      const keyOptions: tIdentityHashKeyOptions = {
        kind: 'person',
        uuid: userId,
        workspaceId: undefined,
      };
      const sdkHoldsUser = this.getDistinctId() === userId;
      // A call without properties only re-asserts the id: sending or storing an
      // empty set would make the next call, with the properties, look changed
      // and send them again.
      if (Object.keys(digest.propertyHashes).length === 0) {
        if (!sdkHoldsUser) posthog.identify(userId, properties, onceProperties);
        return;
      }
      const entry = sdkHoldsUser
        ? readIdentityHashEntry(keyOptions)
        : undefined;

      if (!entry) {
        posthog.identify(userId, properties, onceProperties);
        this.storeSentDigest(keyOptions, digest);
        return;
      }

      const decision = decideSend(personProperties, { digest, entry });
      if (decision.kind === 'none') return;
      if (decision.kind === 'full') {
        posthog.setPersonProperties(personProperties, onceProperties);
      } else {
        posthog.setPersonProperties(decision.changedProperties);
      }
      this.storeSentDigest(keyOptions, digest);
    } catch (error) {
      LLog.error(LOG_CTX, 'PostHog identify failed', error as Error);
    }
  }

  track(event: string | tAnalyticsCapture, properties?: Record<string, unknown>): void {
    try {
      const name = typeof event === 'string' ? event : event.event;
      const payload = typeof event === 'string' ? properties : event.properties;
      const transport = typeof event === 'string' ? undefined : event.transport;
      if (transport) posthog.capture(name, payload, { transport });
      else posthog.capture(name, payload);
    } catch (error) {
      LLog.error(LOG_CTX, 'PostHog capture failed', error as Error);
    }
  }

  group(groupType: string, groupKey: string, properties?: Record<string, unknown>): void {
    try {
      const groupProperties = properties ?? {};
      const digest = buildIdentityDigest(groupProperties);
      // Keyed by the group alone: the group call can run before identify, while
      // the distinct id is still anonymous.
      const keyOptions: tIdentityHashKeyOptions = {
        kind: 'group',
        workspaceId: groupKey,
      };
      const sdkHoldsGroup = posthog.getGroups()?.[groupType] === groupKey;
      const entry = sdkHoldsGroup
        ? readIdentityHashEntry(keyOptions)
        : undefined;
      // A call made before the workspace details load carries no properties.
      // It only re-asserts the group: sending or storing an empty set would make
      // the next call, with the details, look changed and send them again.
      const hasProperties = Object.keys(digest.propertyHashes).length > 0;
      let decision: tSendDecision = { kind: 'full' };
      if (!hasProperties) decision = { kind: 'none' };
      else if (entry) decision = decideSend(groupProperties, { digest, entry });

      if (decision.kind === 'none') {
        // Re-asserts the group on this page load without a $groupidentify.
        posthog.group(groupType, groupKey);
      } else {
        posthog.group(
          groupType,
          groupKey,
          decision.kind === 'full' ? properties : decision.changedProperties,
        );
        this.storeSentDigest(keyOptions, digest);
      }
    } catch (error) {
      LLog.error(LOG_CTX, 'PostHog group failed', error as Error);
    }
  }

  setPersonProperties(properties: Record<string, unknown>): void {
    try {
      posthog.setPersonProperties(properties);
    } catch (error) {
      LLog.error(LOG_CTX, 'PostHog setPersonProperties failed', error as Error);
    }
  }

  register(superProperties: Record<string, unknown>): void {
    try {
      posthog.register(superProperties);
    } catch (error) {
      LLog.error(LOG_CTX, 'PostHog register failed', error as Error);
    }
  }

  unregister(key: string): void {
    try {
      posthog.unregister(key);
    } catch (error) {
      LLog.error(LOG_CTX, 'PostHog unregister failed', error as Error);
    }
  }

  getRegisteredProperty(key: string): unknown {
    try {
      return posthog.get_property(key);
    } catch {
      return undefined;
    }
  }

  registerForSession(sessionProperties: Record<string, unknown>): void {
    try {
      posthog.register_for_session(sessionProperties);
    } catch (error) {
      LLog.error(LOG_CTX, 'PostHog register_for_session failed', error as Error);
    }
  }

  unregisterForSession(key: string): void {
    try {
      posthog.unregister_for_session(key);
    } catch (error) {
      LLog.error(LOG_CTX, 'PostHog unregister_for_session failed', error as Error);
    }
  }

  reset(): void {
    try {
      posthog.reset();
    } catch (error) {
      LLog.error(LOG_CTX, 'PostHog reset failed', error as Error);
    }
  }

  onFeatureFlags(callback: (flags: Record<string, boolean | string>) => void): () => void {
    try {
      return posthog.onFeatureFlags((...args) => {
        const [, flags, context] = args;
        if (!context?.errorsLoading) callback(flags);
      });
    } catch (error) {
      LLog.error(LOG_CTX, 'PostHog onFeatureFlags failed', error as Error);
      return () => undefined;
    }
  }

  onSessionId(callback: (sessionId: string) => void): () => void {
    try {
      return posthog.onSessionId((sessionId) => callback(sessionId));
    } catch (error) {
      LLog.error(LOG_CTX, 'PostHog onSessionId failed', error as Error);
      return () => undefined;
    }
  }

  startSessionRecording(): void {
    if (getPerfArms().replayArm === 'holdout') return;
    try {
      if (!posthog.sessionRecordingStarted()) {
        posthog.startSessionRecording();
      }
    } catch (error) {
      LLog.error(LOG_CTX, 'PostHog startSessionRecording failed', error as Error);
    }
  }

  stopSessionRecording(): void {
    try {
      posthog.stopSessionRecording();
    } catch (error) {
      LLog.error(LOG_CTX, 'PostHog stopSessionRecording failed', error as Error);
    }
  }

  getDistinctId(): string | undefined {
    try {
      return posthog.get_distinct_id();
    } catch {
      return undefined;
    }
  }

  private storeSentDigest(
    keyOptions: tIdentityHashKeyOptions,
    digest: tIdentityDigest,
  ): void {
    // Stored once the SDK call returns: posthog-js reports no delivery, so the
    // resend interval and the distinct id check repair a send that was lost.
    writeIdentityHashEntry(keyOptions, { ...digest, sentAt: Date.now() });
  }
}
